import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, MapPin, Zap } from "lucide-react";
import { BRAND, MEDIA, WHATSAPP_LINK } from "@/lib/data";

function ScarcityBar() {
  const message =
    "⚡ VAGAS LIMITADAS • ESTRATÉGIA INDIVIDUALIZADA • EVOLUÇÃO DE VERDADE • RESULTADADOS REAIS E CONSISTENTES";
  return (
    <div
      data-testid="scarcity-bar"
      className="relative bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 text-black text-[11px] sm:text-xs font-bold tracking-wider uppercase overflow-hidden"
      style={{ height: 32 }}
    >
      <div className="marquee-track items-center h-full">
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className="flex items-center gap-3 px-6 h-full whitespace-nowrap"
          >
            {message}
          </span>
        ))}
      </div>
    </div>
  );
}

function WhatsAppFloat() {
  return (
    <a
      href={WHATSAPP_LINK(
        "Olá Vagner! Vim pelo site da VM Performance e quero conversar."
      )}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-button"
      aria-label="Falar no WhatsApp"
      className="fixed bottom-6 right-6 z-40 group"
    >
      <span className="absolute inset-0 rounded-full bg-[#25D366] pulse-ring" />
      <span className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(37,211,102,0.4)] group-hover:scale-110 transition-transform">
        <svg viewBox="0 0 32 32" className="w-7 h-7 sm:w-8 sm:h-8" fill="currentColor">
          <path d="M19.11 17.28c-.26-.13-1.56-.77-1.8-.86-.24-.09-.42-.13-.6.13-.17.26-.68.86-.83 1.03-.15.17-.31.19-.57.06-.26-.13-1.1-.41-2.09-1.29-.77-.69-1.29-1.54-1.44-1.8-.15-.26-.02-.4.11-.53.11-.11.26-.29.39-.43.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.06-.13-.58-1.39-.79-1.9-.21-.5-.42-.43-.58-.44l-.49-.01a.95.95 0 0 0-.69.32c-.24.26-.9.88-.9 2.15s.92 2.49 1.05 2.66c.13.17 1.82 2.77 4.41 3.88.62.27 1.1.43 1.47.55.62.2 1.18.17 1.62.1.49-.07 1.56-.64 1.78-1.25.22-.62.22-1.15.15-1.25-.06-.11-.24-.17-.5-.3zM16.02 6.67c-5.15 0-9.33 4.18-9.33 9.33 0 1.64.43 3.24 1.25 4.65L6 25.33l4.83-1.27a9.29 9.29 0 0 0 4.47 1.15h.01c5.15 0 9.33-4.18 9.33-9.33s-4.18-9.33-9.33-9.33Zm0 16.82h-.01a7.49 7.49 0 0 1-3.81-1.04l-.27-.16-2.87.75.77-2.79-.18-.29a7.5 7.5 0 1 1 6.37 3.53Z" />
        </svg>
      </span>
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center whitespace-nowrap rounded-full bg-black/80 backdrop-blur border border-white/10 px-3 py-1.5 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity">
        Fale comigo no WhatsApp
      </span>
    </a>
  );
}

function Logo() {
  return (
    <Link
      to="/"
      data-testid="brand-logo"
      className="flex items-center gap-2.5 font-heading font-black text-lg sm:text-xl tracking-tight group"
    >
      <img
        src={MEDIA.logo}
        alt="VM Performance"
        className="w-11 h-11 sm:w-12 sm:h-12 object-contain drop-shadow-[0_0_14px_rgba(250,204,21,0.45)] group-hover:drop-shadow-[0_0_20px_rgba(250,204,21,0.7)] transition-all"
      />
      <span className="text-white leading-none">
        VM <span className="text-gradient-yo">Performance</span>
      </span>
    </Link>
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const navLinkCls = ({ isActive }) =>
    `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
      isActive ? "text-yellow-400" : "text-neutral-300 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{ top: 32 }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-[72px]">
        <Logo />

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={navLinkCls} data-testid="nav-home">
            Início
          </NavLink>
          <NavLink to="/personal" className={navLinkCls} data-testid="nav-personal">
            Personal Trainer
          </NavLink>
          <NavLink to="/online" className={navLinkCls} data-testid="nav-online">
            Consultoria Online
          </NavLink>
        </nav>

        <div className="hidden md:block">
          <a
            href={WHATSAPP_LINK("Olá Vagner! Quero começar meu treino.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-5 py-2.5 text-sm tracking-wide shadow-[0_0_30px_rgba(250,204,21,0.25)] hover:shadow-[0_0_40px_rgba(250,204,21,0.45)] hover:-translate-y-0.5 transition-all"
          >
            Começar agora
          </a>
        </div>

        <button
          data-testid="header-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 text-white hover:bg-white/5"
          aria-label="Abrir menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              <NavLink to="/" end className={navLinkCls} data-testid="mnav-home">
                Início
              </NavLink>
              <NavLink to="/personal" className={navLinkCls} data-testid="mnav-personal">
                Personal Trainer
              </NavLink>
              <NavLink to="/online" className={navLinkCls} data-testid="mnav-online">
                Consultoria Online
              </NavLink>
              <a
                href={WHATSAPP_LINK("Olá Vagner! Quero começar meu treino.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mnav-cta-whatsapp"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-5 py-3 text-sm"
              >
                Começar agora
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-black mt-24"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-3">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-neutral-400 leading-relaxed max-w-xs">
            Personal Trainer Presencial e Consultoria Online com resultado garantido. Transformação de verdade para quem já cansou de métodos falhos.
          </p>
          <p className="mt-4 text-xs text-neutral-500">
            {BRAND.professional} • {BRAND.cref}
          </p>
        </div>

        <div>
          <h4 className="font-heading text-sm uppercase tracking-[0.18em] text-yellow-400">
            Navegação
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-neutral-300">
            <li>
              <Link to="/" className="hover:text-yellow-400">Início</Link>
            </li>
            <li>
              <Link to="/personal" className="hover:text-yellow-400">Personal Trainer</Link>
            </li>
            <li>
              <Link to="/online" className="hover:text-yellow-400">Consultoria Online</Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-sm uppercase tracking-[0.18em] text-yellow-400">
            Contato
          </h4>
          <ul className="mt-4 space-y-3 text-sm text-neutral-300">
            <li>
              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-yellow-400"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" /> @{BRAND.instagram}
              </a>
            </li>
            <li>
              <a
                href={WHATSAPP_LINK("Olá Vagner! Vim pelo site.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-yellow-400"
                data-testid="footer-whatsapp"
              >
                <svg viewBox="0 0 32 32" className="w-4 h-4" fill="currentColor">
                  <path d="M16 3a13 13 0 0 0-11.1 19.7L3 29l6.5-1.7A13 13 0 1 0 16 3Zm7.5 18.3c-.3.9-1.7 1.7-2.4 1.8-.6.1-1.4.2-2.3-.1-.5-.2-1.2-.4-2.1-.8-3.7-1.6-6-5.4-6.2-5.7-.2-.3-1.5-2-1.5-3.9 0-1.9 1-2.8 1.3-3.2.3-.4.7-.5 1-.5h.7c.2 0 .5-.1.8.6.3.7 1 2.4 1 2.6.1.2.1.4 0 .6-.1.2-.2.3-.4.5-.2.2-.4.5-.5.6-.2.2-.4.4-.2.8.2.4.9 1.6 2 2.6 1.4 1.2 2.5 1.6 2.9 1.8.4.2.6.1.8-.1.2-.2.9-1.1 1.2-1.4.2-.4.5-.3.8-.2.3.1 2.1 1 2.4 1.2.4.2.6.3.7.4 0 .2 0 .8-.3 1.5Z" />
                </svg>
                {BRAND.whatsappDisplay}
              </a>
            </li>
            <li className="inline-flex items-start gap-2 text-neutral-400">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{BRAND.gymName} — {BRAND.gymAddress}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-xs text-neutral-500 flex flex-col sm:flex-row gap-2 items-center justify-between">
          <p>© {new Date().getFullYear()} VM Performance. Todos os direitos reservados.</p>
          <p className="uppercase tracking-widest">Feito para quem não espera resultado — busca.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="fixed top-0 left-0 right-0 z-50">
        <ScarcityBar />
      </div>
      <Header />
      <main style={{ paddingTop: 32 }}>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
